import React from "react";
import { Plane, Building2, Map } from "lucide-react";

/**
 * CtaButtonsCard
 *
 * Phase 14.9.14 — lightweight chip-style buttons rendered below the
 * primary vertical's result list. Each chip, on click, emits the
 * pre-composed `message_to_send` into the same chat thread — the chat
 * handler then re-runs with the new intent and surfaces that vertical's
 * cards. The user can therefore "drill into" hotels / itinerary
 * without retyping context. B&W aesthetic, no emoji.
 *
 * Schema (from backend `router_agent._build_cta_card`):
 *   {
 *     type: "cta_buttons",
 *     primary_intent: "flight" | "hotel" | "itinerary",
 *     buttons: [
 *       { label, icon, message_to_send },
 *       ...
 *     ]
 *   }
 *
 * `onSend(text)` is the chat-page handler that submits a new user
 * message in the same conversation (already plumbed for the other
 * `onPlacesToSee` style cards — we reuse it verbatim).
 */
const _ICON = {
    flight: Plane,
    plane: Plane,
    hotel: Building2,
    building: Building2,
    itinerary: Map,
    map: Map,
};

export default function CtaButtonsCard({ card, onSend }) {
    const buttons = Array.isArray(card?.buttons) ? card.buttons : [];
    if (buttons.length === 0) return null;

    return (
        <div
            data-testid="cta-buttons-card"
            className="flex flex-wrap gap-2 my-2"
        >
            {buttons.map((btn, i) => {
                const Icon = _ICON[btn.icon] || Map;
                const msg = btn.message_to_send || btn.label;
                return (
                    <button
                        key={`${btn.label}-${i}`}
                        type="button"
                        onClick={() => onSend && onSend(msg)}
                        data-testid={`cta-btn-${(btn.icon || "x")}`}
                        className={
                            "inline-flex items-center gap-2 px-3 py-1.5 rounded-full " +
                            "border border-neutral-200 bg-white text-neutral-900 " +
                            "text-sm font-medium hover:bg-neutral-50 hover:border-neutral-300 " +
                            "transition-colors"
                        }
                    >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                        <span>{btn.label}</span>
                    </button>
                );
            })}
        </div>
    );
}
